'use client';

import React, { useState, useRef } from 'react';
import {
  Scan, CheckCircle2, AlertTriangle, Package, Hash,
  Calendar, ArrowRight, RotateCcw, Info, Clock,
  MapPin, Layers, ChevronRight, ShoppingCart, SortAsc
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type PickStatus = 'pending' | 'picked' | 'skipped';

interface PickItem {
  id: string;
  barcode: string;
  itemName: string;
  batchNumber: string;
  expiryDate: string;
  expiryTimestamp: number; // for FEFO sort
  quantity: string;
  pickQty: string;
  uom: string;
  rackLocation: string;
  zone: string;
  grnId: string;
  shelfLifePct: number | null;
  status: PickStatus;
  pickedAt?: string;
  fefoRank: number; // 1 = earliest expiry (pick first)
}

// ─── FEFO-sorted QC-approved items ───────────────────────────────────────────
// Sorted ascending by expiry (earliest first = FEFO)

const PICK_ITEMS_RAW: Omit<PickItem, 'fefoRank'>[] = [
  {
    id: 'PICK-001',
    barcode: 'ITM007|BAT-2026-007|30062026|00007',
    itemName: 'Gauze Swab 10x10cm Sterile',
    batchNumber: 'BAT-2026-007',
    expiryDate: '30 Jun 2026',
    expiryTimestamp: new Date('2026-06-30').getTime(),
    quantity: '300',
    pickQty: '50',
    uom: 'PCS',
    rackLocation: 'A-01-02-B-03',
    zone: 'Zone A — Aisle 01',
    grnId: 'GRN-20260611-087',
    shelfLifePct: 42,
    status: 'pending',
  },
  {
    id: 'PICK-002',
    barcode: 'ITM006|BAT-2026-006|15032027|00006',
    itemName: 'Surgical Gloves Size M',
    batchNumber: 'BAT-2026-006',
    expiryDate: '15 Mar 2027',
    expiryTimestamp: new Date('2027-03-15').getTime(),
    quantity: '500',
    pickQty: '100',
    uom: 'PCS',
    rackLocation: 'A-01-01-A-03',
    zone: 'Zone A — Aisle 01 (Fast)',
    grnId: 'GRN-20260612-002',
    shelfLifePct: 75,
    status: 'pending',
  },
  {
    id: 'PICK-003',
    barcode: 'ITM001|BAT-2026-001|01062027|00001',
    itemName: 'Non Woven Tape 75mm x 250cm',
    batchNumber: 'BAT-2026-001',
    expiryDate: '01 Jun 2027',
    expiryTimestamp: new Date('2027-06-01').getTime(),
    quantity: '100',
    pickQty: '20',
    uom: 'ROLL',
    rackLocation: 'A-01-02-B-05',
    zone: 'Zone A — Aisle 01',
    grnId: 'GRN-20260612-001',
    shelfLifePct: 82,
    status: 'pending',
  },
  {
    id: 'PICK-004',
    barcode: 'ITM003|BAT-2026-003|01092028|00003',
    itemName: 'Mayo S Trolley Cover Small',
    batchNumber: 'BAT-2026-003',
    expiryDate: '01 Sep 2028',
    expiryTimestamp: new Date('2028-09-01').getTime(),
    quantity: '200',
    pickQty: '40',
    uom: 'PCS',
    rackLocation: 'B-02-03-A-01',
    zone: 'Zone B — Aisle 02',
    grnId: 'GRN-20260612-001',
    shelfLifePct: 91,
    status: 'pending',
  },
  {
    id: 'PICK-005',
    barcode: 'ITM004|AUTO-345604|NOEXP|00004',
    itemName: 'BP Scalpel Handle Size 4',
    batchNumber: 'AUTO-345604',
    expiryDate: 'No Expiry',
    expiryTimestamp: Number.MAX_SAFE_INTEGER,
    quantity: '20',
    pickQty: '5',
    uom: 'PCS',
    rackLocation: 'C-03-01-B-02',
    zone: 'Zone C — Aisle 03',
    grnId: 'GRN-20260612-002',
    shelfLifePct: null,
    status: 'pending',
  },
];

// Apply FEFO rank (sorted by expiry ascending)
const sorted = [...PICK_ITEMS_RAW].sort((a, b) => a.expiryTimestamp - b.expiryTimestamp);
const PICK_ITEMS: PickItem[] = sorted.map((item, idx) => ({ ...item, fefoRank: idx + 1 }));

// ─── Shelf life bar ───────────────────────────────────────────────────────────

function ShelfLifeBar({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-2xs text-muted-foreground">No Expiry</span>;
  const color = pct >= 80 ? 'bg-primary' : pct >= 50 ? 'bg-accent' : 'bg-red-400';
  const textColor = pct >= 80 ? 'text-primary' : pct >= 50 ? 'text-accent' : 'text-red-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-2xs font-bold tabular-nums ${textColor}`}>{pct}%</span>
    </div>
  );
}

// ─── FEFO rank badge ──────────────────────────────────────────────────────────

function FEFOBadge({ rank }: { rank: number }) {
  const isFirst = rank === 1;
  return (
    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold ${
      isFirst
        ? 'bg-red-500/15 text-red-400 border border-red-500/30'
        : rank <= 2
        ? 'bg-accent/15 text-accent border border-accent/30' :'bg-muted/50 text-muted-foreground border border-border'
    }`}>
      <SortAsc size={9} />
      FEFO #{rank}
    </div>
  );
}

// ─── Main board ───────────────────────────────────────────────────────────────

export default function PickingBoard() {
  const [items, setItems] = useState<PickItem[]>(PICK_ITEMS);
  const [scanInput, setScanInput] = useState('');
  const [activeItem, setActiveItem] = useState<PickItem | null>(null);
  const [confirmState, setConfirmState] = useState<'idle' | 'awaiting' | 'confirmed' | 'mismatch'>('idle');
  const [flashType, setFlashType] = useState<'success' | 'error' | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const [confirmScanInput, setConfirmScanInput] = useState('');

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const pickedCount = items.filter((i) => i.status === 'picked').length;

  // Handle item barcode scan to load pick task
  const handleItemScan = (e: React.FormEvent) => {
    e.preventDefault();
    const val = scanInput.trim();
    if (!val) return;

    const found = items.find(
      (i) =>
        i.barcode === val ||
        i.id === val ||
        i.batchNumber === val ||
        i.barcode.startsWith(val.split('|')[0])
    );

    if (!found) {
      setFlashType('error');
      setTimeout(() => setFlashType(null), 1200);
      setScanInput('');
      return;
    }

    if (found.status === 'picked') {
      setScanInput('');
      return;
    }

    setActiveItem(found);
    setConfirmState('awaiting');
    setConfirmScanInput('');
    setScanInput('');
    setTimeout(() => confirmRef.current?.focus(), 100);
  };

  // Handle confirmation scan — operator scans item barcode again at rack to confirm pick
  const handleConfirmScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeItem) return;
    const val = confirmScanInput.trim();
    if (!val) return;

    const isMatch =
      val === activeItem.barcode ||
      val === activeItem.id ||
      val === activeItem.batchNumber ||
      activeItem.barcode.startsWith(val.split('|')[0]);

    if (isMatch) {
      const now = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      setItems((prev) =>
        prev.map((i) =>
          i.id === activeItem.id ? { ...i, status: 'picked', pickedAt: now } : i
        )
      );
      setConfirmState('confirmed');
      setFlashType('success');
      setTimeout(() => {
        setFlashType(null);
        setActiveItem(null);
        setConfirmState('idle');
        setConfirmScanInput('');
        scanRef.current?.focus();
      }, 2000);
    } else {
      setConfirmState('mismatch');
      setFlashType('error');
      setTimeout(() => {
        setFlashType(null);
        setConfirmState('awaiting');
        setConfirmScanInput('');
        confirmRef.current?.focus();
      }, 1500);
    }
  };

  const handleSkip = () => {
    if (!activeItem) return;
    setItems((prev) =>
      prev.map((i) => (i.id === activeItem.id ? { ...i, status: 'skipped' } : i))
    );
    setActiveItem(null);
    setConfirmState('idle');
    setConfirmScanInput('');
    setTimeout(() => scanRef.current?.focus(), 100);
  };

  const handleReset = (id: string) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: 'pending', pickedAt: undefined } : i))
    );
  };

  const handleSelectItem = (item: PickItem) => {
    if (item.status !== 'pending') return;
    setActiveItem(item);
    setConfirmState('awaiting');
    setConfirmScanInput('');
    setTimeout(() => confirmRef.current?.focus(), 100);
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-0 relative overflow-hidden">

      {/* Flash overlay */}
      {flashType && (
        <div
          className={`fixed inset-0 z-50 pointer-events-none transition-opacity duration-300 ${
            flashType === 'success' ? 'bg-primary/20' : 'bg-red-500/20'
          }`}
        />
      )}

      {/* ── Left panel: pick queue ── */}
      <div className="w-full lg:w-80 xl:w-96 border-r border-border bg-card flex flex-col flex-shrink-0">

        {/* Stats */}
        <div className="px-4 py-3 border-b border-border grid grid-cols-3 gap-2">
          <div className="bg-muted/40 rounded-lg px-2 py-2 text-center">
            <p className="text-lg font-bold text-accent tabular-nums">{pendingCount}</p>
            <p className="text-2xs text-muted-foreground">Pending</p>
          </div>
          <div className="bg-muted/40 rounded-lg px-2 py-2 text-center">
            <p className="text-lg font-bold text-primary tabular-nums">{pickedCount}</p>
            <p className="text-2xs text-muted-foreground">Picked</p>
          </div>
          <div className="bg-muted/40 rounded-lg px-2 py-2 text-center">
            <p className="text-lg font-bold text-foreground tabular-nums">{items.length}</p>
            <p className="text-2xs text-muted-foreground">Total</p>
          </div>
        </div>

        {/* FEFO label */}
        <div className="px-4 py-2 border-b border-border flex items-center gap-2">
          <SortAsc size={12} className="text-red-400" />
          <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">
            Sorted by FEFO — earliest expiry first
          </span>
        </div>

        {/* Item scan input */}
        <div className="px-4 py-3 border-b border-border">
          <form onSubmit={handleItemScan} className="flex gap-2">
            <div className="flex-1 relative">
              <Scan size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={scanRef}
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                placeholder="Scan item barcode…"
                className="w-full bg-muted/40 border border-border rounded-lg pl-8 pr-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition-all font-mono"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2.5 rounded-lg bg-accent text-accent-foreground text-xs font-bold hover:bg-accent/90 transition-all flex items-center gap-1"
            >
              <ArrowRight size={14} />
            </button>
          </form>
          <p className="text-2xs text-muted-foreground mt-1.5 px-1">Or tap any pending item below</p>
        </div>

        {/* Item list — FEFO ordered */}
        <div className="flex-1 overflow-y-auto py-2 space-y-1 px-2">
          {items.map((item) => {
            const isActive = activeItem?.id === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectItem(item)}
                disabled={item.status !== 'pending'}
                className={`w-full text-left px-3 py-3 rounded-xl border transition-all ${
                  isActive
                    ? 'border-accent bg-accent/8 ring-1 ring-accent/30'
                    : item.status === 'picked' ?'border-primary/20 bg-primary/3 opacity-70'
                    : item.status === 'skipped' ?'border-border bg-muted/20 opacity-50' :'border-border bg-card hover:bg-muted/30 hover:border-accent/30 cursor-pointer'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <FEFOBadge rank={item.fefoRank} />
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {item.status === 'picked' && (
                      <span className="badge-available text-2xs font-bold px-1.5 py-0.5 rounded-full">PICKED</span>
                    )}
                    {item.status === 'skipped' && (
                      <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">SKIPPED</span>
                    )}
                    {item.status === 'pending' && (
                      <span className="badge-pending text-2xs font-bold px-1.5 py-0.5 rounded-full">PENDING</span>
                    )}
                  </div>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground leading-tight truncate">{item.itemName}</p>
                    <p className="text-2xs font-mono text-accent mt-0.5">{item.batchNumber}</p>
                    <p className="text-2xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <MapPin size={9} />
                      {item.rackLocation}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-foreground">{item.pickQty} <span className="text-muted-foreground font-normal text-2xs">{item.uom}</span></p>
                    {item.status === 'picked' && item.pickedAt && (
                      <p className="text-2xs text-muted-foreground mt-0.5 flex items-center gap-1 justify-end">
                        <Clock size={9} />
                        {item.pickedAt}
                      </p>
                    )}
                  </div>
                </div>
                {item.status === 'picked' && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleReset(item.id); }}
                    className="mt-1.5 text-2xs text-muted-foreground hover:text-foreground underline transition-colors"
                  >
                    Reset
                  </button>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right panel: pick task + confirm ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-background">

        {/* Idle state */}
        {confirmState === 'idle' && (
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-2xl bg-muted/40 flex items-center justify-center mx-auto mb-5">
              <ShoppingCart size={36} className="text-muted-foreground/40" />
            </div>
            <h2 className="text-lg font-bold text-foreground mb-2">Ready to Pick</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Scan an item barcode from the left panel or tap a pending item. Items are sorted by FEFO — earliest expiry is picked first.
            </p>
            <div className="mt-6 space-y-2.5 text-left">
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-card border border-border">
                <div className="w-6 h-6 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-2xs font-black text-red-400">1</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">Scan item barcode</p>
                  <p className="text-2xs text-muted-foreground mt-0.5">Load the pick task and rack location</p>
                </div>
              </div>
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-card border border-border">
                <div className="w-6 h-6 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-2xs font-black text-accent">2</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">Go to rack location</p>
                  <p className="text-2xs text-muted-foreground mt-0.5">Navigate to the assigned rack and pick the item</p>
                </div>
              </div>
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-card border border-border">
                <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-2xs font-black text-primary">3</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">Scan to confirm pick</p>
                  <p className="text-2xs text-muted-foreground mt-0.5">Scan item barcode again to mark as Picked</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Awaiting confirmation scan */}
        {(confirmState === 'awaiting' || confirmState === 'mismatch') && activeItem && (
          <div className="w-full max-w-lg space-y-4">

            {/* Item info card */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center flex-shrink-0">
                  <Package size={20} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground leading-tight">{activeItem.itemName}</p>
                  <p className="text-2xs font-mono text-accent mt-0.5">{activeItem.barcode}</p>
                </div>
                <FEFOBadge rank={activeItem.fefoRank} />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Hash size={10} /> Batch</p>
                  <p className="text-xs font-mono font-bold text-accent">{activeItem.batchNumber}</p>
                </div>
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Calendar size={10} /> Expiry</p>
                  <p className="text-xs font-semibold text-foreground">{activeItem.expiryDate}</p>
                </div>
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Pick Qty</p>
                  <p className="text-xs font-bold text-foreground">{activeItem.pickQty} <span className="text-muted-foreground font-normal">{activeItem.uom}</span></p>
                </div>
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Shelf Life</p>
                  <ShelfLifeBar pct={activeItem.shelfLifePct} />
                </div>
              </div>
            </div>

            {/* Rack location card */}
            <div className="bg-card border-2 border-accent/40 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={15} className="text-accent" />
                <span className="text-xs font-bold uppercase tracking-wider text-accent">Go to Rack Location</span>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Rack</p>
                  <p className="text-3xl font-black font-mono text-foreground tracking-wider">{activeItem.rackLocation}</p>
                </div>
                <ChevronRight size={20} className="text-muted-foreground/40" />
                <div className="text-right">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1 justify-end"><Layers size={10} /> Zone</p>
                  <p className="text-xs font-semibold text-foreground">{activeItem.zone}</p>
                  <p className="text-2xs text-muted-foreground mt-0.5">{activeItem.grnId}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-muted/30">
                <Info size={12} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-2xs text-muted-foreground">
                  Pick <span className="font-bold text-foreground">{activeItem.pickQty} {activeItem.uom}</span> of batch <span className="font-mono font-bold text-accent">{activeItem.batchNumber}</span> from this rack
                </p>
              </div>
            </div>

            {/* Confirm scan card */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <p className="text-sm font-bold text-foreground mb-1">Confirm Pick</p>
              <p className="text-2xs text-muted-foreground mb-3">
                Scan the item barcode again at the rack to confirm pick and mark as <span className="font-bold text-primary">Picked</span>
              </p>
              <form onSubmit={handleConfirmScan} className="flex gap-2">
                <div className="flex-1 relative">
                  <Scan size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={confirmRef}
                    value={confirmScanInput}
                    onChange={(e) => setConfirmScanInput(e.target.value)}
                    placeholder="Scan item barcode to confirm…"
                    className={`w-full bg-muted/40 border rounded-xl pl-8 pr-3 py-3 text-sm font-mono text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 transition-all ${
                      confirmState === 'mismatch' ?'border-red-500 ring-red-500/30 bg-red-500/5' :'border-border focus:ring-primary focus:border-primary'
                    }`}
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-all flex items-center gap-2"
                >
                  <CheckCircle2 size={16} />
                  Confirm
                </button>
              </form>
              {confirmState === 'mismatch' && (
                <div className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertTriangle size={13} className="text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-400 font-semibold">Barcode mismatch — scan the correct item barcode</p>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-xs text-muted-foreground hover:text-foreground underline transition-colors"
                >
                  Skip this item
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveItem(null);
                    setConfirmState('idle');
                    setConfirmScanInput('');
                  }}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw size={12} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmed state */}
        {confirmState === 'confirmed' && activeItem && (
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={40} className="text-primary" />
            </div>
            <h2 className="text-xl font-black text-primary mb-2">Pick Confirmed!</h2>
            <p className="text-sm text-foreground font-semibold mb-1">{activeItem.itemName}</p>
            <p className="text-xs font-mono text-accent mb-1">{activeItem.batchNumber}</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="badge-available text-xs font-bold px-3 py-1 rounded-full">PICKED</span>
              <span className="text-xs text-muted-foreground">Ready for Kitting</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
